"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRenderLabels = exports.useRenderLabelsForPanel = void 0;
const tslib_1 = require("tslib");
/* eslint-disable deprecation/deprecation */
const client_1 = require("@apollo/client");
const catalog_core_1 = require("@tableau/catalog-core");
const catalog_messages_1 = require("@tableau/catalog-messages");
const widgets_1 = require("@tableau/widgets");
const react_query_1 = require("@tanstack/react-query");
const react_1 = tslib_1.__importDefault(require("react"));
const useTelemetrySender_1 = require("../../hooks/useTelemetrySender");
const useToggle_1 = require("../../hooks/useToggle");
const TelemetryContext_1 = require("../../telemetry/TelemetryContext");
const useLabelDialogInitialState_1 = require("../dialog/useLabelDialogInitialState");
const LabelButtons_1 = require("../LabelButtons");
const LabelHelpers_1 = require("../LabelHelpers");
const LabelPopover_1 = require("../LabelPopover");
const LabelValueSorting_1 = require("../LabelValueSorting");
const labelCategories_1 = require("../restApi/labelCategories");
const Labels_1 = require("../restApi/Labels");
const LabelTypes_1 = require("../restApi/LabelTypes");
const labelValues_1 = require("../restApi/labelValues");
const LabelCategories_1 = require("../shared/LabelCategories");
const Styles_1 = require("../Styles");
const getUsers_1 = require("./getUsers");
const getLabelsGraph_1 = require("./lazyQueries/getLabelsGraph");
const StickersFlexBox_1 = require("./StickersFlexBox");
const popoverNoOpReducer = (s) => {
    return s;
};
function usePanelReducer(renderMode, elevated) {
    const [state, setState] = react_1.default.useState({ elevated });
    const panelModeReducer = react_1.default.useCallback((s, a) => {
        switch (a.type) {
            case 'click': {
                if (s.showing === a.clicked) {
                    return { elevated: false, showing: undefined };
                }
                else {
                    return { elevated: false, showing: a.clicked };
                }
            }
            case 'severe': {
                if (s.elevated || s.showing === undefined) {
                    return { elevated: false, showing: a.category };
                }
                else {
                    return s;
                }
            }
            default:
                (0, catalog_core_1.assertUnreachable)(a);
                return s;
        }
    }, []);
    const reducer = renderMode === 'popover' ? popoverNoOpReducer : panelModeReducer;
    const dispatch = react_1.default.useCallback((action) => setState(prev => reducer(prev, action)), [
        reducer,
        setState,
    ]);
    return [state, dispatch];
}
const defaultChipDisplayCount = 8;
function getRestLabels({ makeJsonRestApiRequest, items }) {
    var _a;
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        if (!items.contentType || !(0, LabelTypes_1.isLabelContentType)(items.contentType) || !items.contentLuid) {
            return [];
        }
        const result = yield (0, Labels_1.getLabelsSimple)({
            contentItems: [{
                    contentId: items.contentLuid,
                    contentType: items.contentType,
                    contentName: items.contentName,
                }],
            makeJsonRestApiRequest,
            transform: (t) => t,
        });
        return (_a = [...result].sort((a, b) => a.id.localeCompare(b.id))) !== null && _a !== void 0 ? _a : [];
    });
}
function mapRestLabelsToRenderProps(labels, authors) {
    const activeLabels = labels ? labels.filter((p) => p.active) : [];
    const customLabelResponse = activeLabels.filter((p) => !(0, LabelCategories_1.isBuiltInLabelCategory)(p.category));
    const customLabels = new Map();
    customLabelResponse.forEach(s => {
        var _a, _b, _c, _d, _e, _f, _g;
        !customLabels.has(s.category)
            ? customLabels.set(s.category, [{
                    type: s.value,
                    active: s.active,
                    author: (_c = authors === null || authors === void 0 ? void 0 : authors.get((_b = (_a = s.owner) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '')) !== null && _c !== void 0 ? _c : (s.userDisplayName ? { username: '', name: s.userDisplayName, domain: '' } : undefined),
                    message: s.message,
                    updatedAt: catalog_messages_1.Formatters.formatMediumDateTimeWithoutSeconds(new Date(s.updatedAt)),
                }])
            : (_d = customLabels.get(s.category)) === null || _d === void 0 ? void 0 : _d.push({
                type: s.value,
                active: s.active,
                author: (_g = authors === null || authors === void 0 ? void 0 : authors.get((_f = (_e = s.owner) === null || _e === void 0 ? void 0 : _e.id) !== null && _f !== void 0 ? _f : '')) !== null && _g !== void 0 ? _g : (s.userDisplayName ? { username: '', name: s.userDisplayName, domain: '' } : undefined),
                message: s.message,
                updatedAt: catalog_messages_1.Formatters.formatMediumDateTimeWithoutSeconds(new Date(s.updatedAt)),
            });
    });
    return ({
        warnings: activeLabels.filter((p) => p.category.toLowerCase() === 'warning').map(warning => {
            var _a, _b, _c;
            return {
                severe: warning.elevated,
                type: warning.value,
                active: warning.active,
                author: (_c = authors === null || authors === void 0 ? void 0 : authors.get((_b = (_a = warning.owner) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '')) !== null && _c !== void 0 ? _c : (warning.userDisplayName ? { username: '', name: warning.userDisplayName, domain: '' } : undefined),
                message: warning.message,
                updatedAt: catalog_messages_1.Formatters.formatMediumDateTimeWithoutSeconds(new Date(warning.updatedAt)),
            };
        }),
        certifications: activeLabels
            .filter((p) => p.category.toLowerCase() === 'certification')
            .map(c => {
            var _a, _b, _c;
            return {
                type: c.value,
                active: c.active,
                author: (_c = authors === null || authors === void 0 ? void 0 : authors.get((_b = (_a = c.owner) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '')) !== null && _c !== void 0 ? _c : (c.userDisplayName ? { username: '', name: c.userDisplayName, domain: '' } : undefined),
                message: c.message,
                updatedAt: catalog_messages_1.Formatters.formatMediumDateTimeWithoutSeconds(new Date(c.updatedAt)),
            };
        }),
        sensitivities: activeLabels
            .filter((p) => p.category.toLowerCase() === 'sensitivity')
            .map(s => {
            var _a, _b, _c;
            return {
                severe: s.elevated,
                type: s.value,
                active: s.active,
                author: (_c = authors === null || authors === void 0 ? void 0 : authors.get((_b = (_a = s.owner) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '')) !== null && _c !== void 0 ? _c : (s.userDisplayName ? { username: '', name: s.userDisplayName, domain: '' } : undefined),
                message: s.message,
                updatedAt: catalog_messages_1.Formatters.formatMediumDateTimeWithoutSeconds(new Date(s.updatedAt)),
            };
        }),
        customLabels: customLabels,
    });
}
function useLabels(_a) {
    var _b, _c, _d, _e;
    var { content: item } = _a, params = tslib_1.__rest(_a, ["content"]);
    const client = (0, client_1.useApolloClient)();
    const labelsFn = (0, catalog_core_1.useRestFn)(getRestLabels);
    const categoriesFn = (0, catalog_core_1.useRestFnNoParams)(labelCategories_1.getLabelCategories);
    const valuesFn = (0, catalog_core_1.useRestFn)(labelValues_1.getLabelValues);
    const [enabled, setEnabled] = react_1.default
        .useState(params.probablyOrEager === 'eager' || ((_b = params.probablyOrEager) === null || _b === void 0 ? void 0 : _b.resolve) || false);
    const { customLabelCategoriesEnabled } = (0, catalog_core_1.useContextWithOverride)(catalog_core_1.FeatureFlagsContext, (_c = params.contextOverrides) === null || _c === void 0 ? void 0 : _c.featureFlags);
    const invoke = react_1.default.useCallback(() => setEnabled(true), [setEnabled]);
    const graphEnabled = params.catalogEnabled && enabled && !!(params.includeParented || params.includeUpstream);
    const [valuesResp, categoriesResp, labelsResp, graphResp] = (0, react_query_1.useQueries)({
        queries: [
            {
                refetchOnWindowFocus: false,
                queryKey: ['values'],
                queryFn: () => valuesFn({}),
                enabled,
            },
            {
                refetchOnWindowFocus: false,
                queryKey: ['categories'],
                queryFn: () => categoriesFn(),
                enabled,
            },
            {
                refetchOnWindowFocus: false,
                queryKey: ['labels', item.contentLuid],
                queryFn: () => labelsFn({ items: item }),
                enabled,
                initialData: [],
                placeholderData: [],
            },
            {
                refetchOnWindowFocus: false,
                queryKey: ['graph', 'labels', item.contentLuid, item.contentId],
                queryFn: () => (0, getLabelsGraph_1.getLabelsGraph)(Object.assign({ item, client }, params)),
                enabled: graphEnabled,
                placeholderData: {},
                initialData: {},
            },
        ],
    });
    const labelDetails = (0, useLabelDialogInitialState_1.useDetails)(valuesResp.isLoading || categoriesResp.isLoading, valuesResp.data, categoriesResp.data);
    const _labels = (_d = labelsResp.data) !== null && _d !== void 0 ? _d : [];
    const { users } = (0, getUsers_1.useGetUsers)(new Set((0, catalog_core_1.filterOutNullAndUndefined)((_e = _labels === null || _labels === void 0 ? void 0 : _labels.map(l => { var _a; return (_a = l === null || l === void 0 ? void 0 : l.owner) === null || _a === void 0 ? void 0 : _a.id; })) !== null && _e !== void 0 ? _e : [])));
    const { warnings, certifications, sensitivities, customLabels } = mapRestLabelsToRenderProps(_labels, users);
    const warningProps = warnings.length > 0 && params.catalogEnabled
        ? {
            category: 'warning',
            categoryType: 'warning',
            content: item,
            kind: 'hydrated',
            anchored: {
                category: 'warning',
                labels: warnings,
            },
        }
        : undefined;
    const certificationProps = certifications.length > 0
        ? {
            category: 'certification',
            categoryType: 'certification',
            content: item,
            kind: 'hydrated',
            anchored: {
                category: 'certification',
                labels: certifications,
            },
        }
        : undefined;
    const sensitivityProps = sensitivities.length > 0 && params.catalogEnabled
        ? {
            category: 'sensitivity',
            categoryType: 'sensitivity',
            content: item,
            kind: 'hydrated',
            anchored: {
                category: 'sensitivity',
                labels: sensitivities,
            },
        }
        : undefined;
    const customProps = new Map([...customLabels.entries()].map(([category, labels]) => [category, {
            categoryType: 'custom',
            kind: 'hydrated',
            category,
            content: item,
            anchored: {
                category,
                labels,
            },
        }]));
    const rest = {
        loading: labelsResp.isLoading || valuesResp.isLoading || categoriesResp.isLoading,
        warnings: warningProps,
        certifications: certificationProps,
        sensitivities: sensitivityProps,
        customLabels: customProps,
        labelDetails,
    };
    const graph = graphResp.data
        ? Object.assign({ loading: graphResp.isLoading }, graphResp.data) : {};
    const result = merge(rest, graph, item, customLabelCategoriesEnabled);
    return [invoke, result];
}
function merge(rest, graph, content, customLabelCategoriesEnabled) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
    let certifications = undefined;
    let warnings = undefined;
    let sensitivities = undefined;
    const customLabels = new Map();
    if (((_a = graph.certifications) === null || _a === void 0 ? void 0 : _a.parented) || ((_b = rest.certifications) === null || _b === void 0 ? void 0 : _b.anchored)) {
        certifications = {
            category: 'certification',
            categoryType: 'certification',
            content: content,
            kind: 'hydrated',
            anchored: (_c = rest.certifications) === null || _c === void 0 ? void 0 : _c.anchored,
            parented: (_d = graph.certifications) === null || _d === void 0 ? void 0 : _d.parented,
        };
    }
    if (((_e = graph.warnings) === null || _e === void 0 ? void 0 : _e.parented) || ((_f = graph.warnings) === null || _f === void 0 ? void 0 : _f.upstream) || ((_g = rest.warnings) === null || _g === void 0 ? void 0 : _g.anchored)) {
        warnings = {
            category: 'warning',
            categoryType: 'warning',
            content: content,
            kind: 'hydrated',
            upstream: (_h = graph.warnings) === null || _h === void 0 ? void 0 : _h.upstream,
            anchored: (_j = rest.warnings) === null || _j === void 0 ? void 0 : _j.anchored,
            parented: (_k = graph.warnings) === null || _k === void 0 ? void 0 : _k.parented,
        };
    }
    if ((((_l = graph.sensitivities) === null || _l === void 0 ? void 0 : _l.upstream) || ((_m = graph.sensitivities) === null || _m === void 0 ? void 0 : _m.parented) || ((_o = rest.sensitivities) === null || _o === void 0 ? void 0 : _o.anchored))) {
        sensitivities = {
            category: 'sensitivity',
            categoryType: 'sensitivity',
            content: content,
            kind: 'hydrated',
            upstream: (_p = graph.sensitivities) === null || _p === void 0 ? void 0 : _p.upstream,
            anchored: (_q = rest.sensitivities) === null || _q === void 0 ? void 0 : _q.anchored,
            parented: (_r = graph.sensitivities) === null || _r === void 0 ? void 0 : _r.parented,
        };
    }
    // merge graph map and rest map
    if (customLabelCategoriesEnabled && (graph.customLabels || rest.customLabels)) {
        const customCategories = (0, LabelValueSorting_1.sortCategories)(new Set([
            ...((_t = (_s = graph.customLabels) === null || _s === void 0 ? void 0 : _s.keys()) !== null && _t !== void 0 ? _t : []),
            ...((_v = (_u = rest.customLabels) === null || _u === void 0 ? void 0 : _u.keys()) !== null && _v !== void 0 ? _v : []),
        ]));
        customCategories.forEach((category) => {
            var _a, _b;
            const graphCustomLabels = (_a = graph.customLabels) === null || _a === void 0 ? void 0 : _a.get(category);
            const restCustomLabels = (_b = rest.customLabels) === null || _b === void 0 ? void 0 : _b.get(category);
            customLabels.set(category, {
                category: category,
                categoryType: 'custom',
                content: content,
                kind: 'hydrated',
                upstream: graphCustomLabels === null || graphCustomLabels === void 0 ? void 0 : graphCustomLabels.upstream,
                anchored: restCustomLabels === null || restCustomLabels === void 0 ? void 0 : restCustomLabels.anchored,
                parented: graphCustomLabels === null || graphCustomLabels === void 0 ? void 0 : graphCustomLabels.parented,
            });
        });
    }
    return {
        loading: (_w = (rest.loading || graph.loading)) !== null && _w !== void 0 ? _w : undefined,
        error: (_y = (_x = rest.error) !== null && _x !== void 0 ? _x : graph.error) !== null && _y !== void 0 ? _y : undefined,
        certifications: certifications,
        warnings: warnings,
        sensitivities: sensitivities,
        customLabels: customLabels,
        labelDetails: (_z = rest.labelDetails) !== null && _z !== void 0 ? _z : undefined,
    };
}
function isSevere(probably, data) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (probably !== 'eager' && ((probably === null || probably === void 0 ? void 0 : probably.warning) || (probably === null || probably === void 0 ? void 0 : probably.sensitivity))) {
        return (_d = (_b = (_a = probably === null || probably === void 0 ? void 0 : probably.warning) === null || _a === void 0 ? void 0 : _a.severe) !== null && _b !== void 0 ? _b : (_c = probably === null || probably === void 0 ? void 0 : probably.sensitivity) === null || _c === void 0 ? void 0 : _c.severe) !== null && _d !== void 0 ? _d : false;
    }
    if (!data) {
        return false;
    }
    if ((_e = data === null || data === void 0 ? void 0 : data.anchored) === null || _e === void 0 ? void 0 : _e.labels.some(t => t.severe)) {
        return true;
    }
    if ((_f = data === null || data === void 0 ? void 0 : data.upstream) === null || _f === void 0 ? void 0 : _f.labels.some(t => t.severe)) {
        return true;
    }
    if ((_g = data === null || data === void 0 ? void 0 : data.parented) === null || _g === void 0 ? void 0 : _g.labels.some(t => t.severe)) {
        return true;
    }
    return false;
}
function labelValue(defaultType, probably, data) {
    var _a;
    if (probably === null || probably === void 0 ? void 0 : probably.warning) {
        return probably.warning.value;
    }
    let type = defaultType;
    (_a = data === null || data === void 0 ? void 0 : data.anchored) === null || _a === void 0 ? void 0 : _a.labels.forEach(label => {
        type = label.type.toString();
    });
    return type;
}
function useWarnings({ warnings, loading }, _a, invoke, emitTelemetry) {
    var { catalogEnabled, content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented, renderMode } = _a, shared = tslib_1.__rest(_a, ["catalogEnabled", "content", "probablyOrEager", "includeUpstream", "includeParented", "renderMode"]);
    const probably = probablyOrEager;
    const onClick = react_1.default.useCallback(() => {
        if (!catalogEnabled) {
            return;
        }
        invoke('warning');
        emitTelemetry({ elevated: isSevere(probably, warnings), value: labelValue('WARNING', probably, warnings) });
    }, [catalogEnabled, invoke, emitTelemetry, probably, warnings]);
    if ((!warnings || loading) && (probably === null || probably === void 0 ? void 0 : probably.warning)) {
        return (react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({ category: 'warning', categoryType: 'warning', kind: 'dehydrated', severe: isSevere(probably, warnings), content: content }, shared, { loading: loading !== null && loading !== void 0 ? loading : false, onClick: onClick, count: probably.warning.count, labelValue: probably.warning.value, renderMode: renderMode })));
    }
    if (warnings) {
        return (react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({}, warnings, shared, { loading: loading !== null && loading !== void 0 ? loading : false, onClick: onClick, renderMode: renderMode })));
    }
    return null;
}
function useCertifications({ certifications, loading }, _a, invoke, emitTelemetry) {
    var { content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented, renderMode } = _a, shared = tslib_1.__rest(_a, ["content", "probablyOrEager", "includeUpstream", "includeParented", "renderMode"]);
    const probably = probablyOrEager;
    const onClick = react_1.default.useCallback(() => {
        invoke('certified');
        emitTelemetry({ elevated: false, value: labelValue('CERTIFIED', probably, certifications) });
    }, [certifications, emitTelemetry, invoke, probably]);
    if ((!certifications || loading) && (probably === null || probably === void 0 ? void 0 : probably.certified)) {
        return (react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({ category: 'certification', categoryType: 'certification', kind: 'dehydrated', content: content }, shared, { onClick: onClick, loading: loading !== null && loading !== void 0 ? loading : false, renderMode: renderMode })));
    }
    if (certifications) {
        return (react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({}, certifications, shared, { onClick: onClick, loading: loading !== null && loading !== void 0 ? loading : false, renderMode: renderMode })));
    }
    return null;
}
function useSensitivities({ sensitivities, loading }, _a, invoke, emitTelemetry) {
    var { catalogEnabled, content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented, renderMode } = _a, shared = tslib_1.__rest(_a, ["catalogEnabled", "content", "probablyOrEager", "includeUpstream", "includeParented", "renderMode"]);
    const probably = probablyOrEager;
    const onClick = react_1.default.useCallback(() => {
        if (!catalogEnabled) {
            return;
        }
        invoke('sensitivity');
        emitTelemetry({ elevated: false, value: labelValue('SENSITIVE_DATA', probably, sensitivities) });
    }, [catalogEnabled, invoke, emitTelemetry, probably, sensitivities]);
    if ((!sensitivities || loading) && (probably === null || probably === void 0 ? void 0 : probably.sensitivity)) {
        return (react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({ severe: probably.sensitivity.severe, category: 'sensitivity', categoryType: 'sensitivity', kind: 'dehydrated', content: content }, shared, { onClick: onClick, loading: loading !== null && loading !== void 0 ? loading : false, count: probably.sensitivity.count, labelValue: probably.sensitivity.value, renderMode: renderMode })));
    }
    if (sensitivities) {
        return (react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({}, sensitivities, shared, { onClick: onClick, loading: loading !== null && loading !== void 0 ? loading : false, renderMode: renderMode })));
    }
    return null;
}
function useCustom({ customLabels, loading }, _a, invoke, emitTelemetry) {
    var _b, _c, _d;
    var { catalogEnabled, content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented, renderMode } = _a, shared = tslib_1.__rest(_a, ["catalogEnabled", "content", "probablyOrEager", "includeUpstream", "includeParented", "renderMode"]);
    const probably = probablyOrEager;
    const onClick = react_1.default.useCallback((clicked) => {
        var _a;
        if (!catalogEnabled) {
            return;
        }
        invoke(clicked);
        emitTelemetry({
            elevated: false,
            value: labelValue('CUSTOM', probably, Array.from((_a = customLabels === null || customLabels === void 0 ? void 0 : customLabels.values()) !== null && _a !== void 0 ? _a : [])[0]),
        });
    }, [catalogEnabled, invoke, emitTelemetry, probably, customLabels]);
    if ((!customLabels || customLabels.size < 1 || loading) && (probably === null || probably === void 0 ? void 0 : probably.custom)) {
        const numChips = probably.custom.length;
        const numChipstoDisplay = (_b = shared.chipDisplayCount) !== null && _b !== void 0 ? _b : defaultChipDisplayCount;
        const dehydratedCustomLabels = [];
        probably.custom.forEach((e) => {
            dehydratedCustomLabels.push(react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({ category: e.category, categoryType: 'custom', kind: 'dehydrated', content: content }, shared, { onClick: () => onClick(e.category), loading: loading !== null && loading !== void 0 ? loading : false, count: e.count, labelValue: e.value, renderMode: renderMode })));
        });
        return createFlexCustom({
            labels: dehydratedCustomLabels,
            numChips: numChips,
            numChipsToDisplay: numChipstoDisplay,
        });
    }
    if (customLabels) {
        const customLabelPropsList = [...((_c = customLabels === null || customLabels === void 0 ? void 0 : customLabels.values()) !== null && _c !== void 0 ? _c : [])];
        const customLabelsList = [];
        customLabelPropsList.forEach((e) => {
            const element = (react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({}, e, shared, { onClick: () => onClick(e.category), renderMode: renderMode, loading: loading !== null && loading !== void 0 ? loading : false })));
            customLabelsList.push(element);
        });
        const numChips = customLabelsList.length;
        const numChipsToDisplay = (_d = shared.chipDisplayCount) !== null && _d !== void 0 ? _d : defaultChipDisplayCount;
        return createFlexCustom({ labels: customLabelsList, numChips: numChips, numChipsToDisplay });
    }
    return null;
}
function createFlexCustom(props) {
    const { labels, numChips: numChips, numChipsToDisplay } = props;
    const displayedCustomLabels = (numChips > 0)
        ? (react_1.default.createElement(react_1.default.Fragment, null, labels.map((i, index) => {
            if (index < numChipsToDisplay) {
                return react_1.default.createElement(Wrapper, { key: index }, i);
            }
            else {
                return null;
            }
        })))
        : undefined;
    const hiddenCustomLabels = (numChipsToDisplay <= numChips)
        ? (react_1.default.createElement(react_1.default.Fragment, null, labels.map((i, index) => {
            if (index >= numChipsToDisplay) {
                return react_1.default.createElement(Wrapper, { key: index }, i);
            }
            else {
                return null;
            }
        })))
        : undefined;
    return {
        displayedCustomLabels: displayedCustomLabels,
        hiddenCustomLabels: hiddenCustomLabels,
        numCustomLabels: numChips,
    };
}
function useBuildTelemetryEvents(content_Type, testId, source, telemetryContext) {
    const telemetry = (0, catalog_core_1.useContextWithOverride)(TelemetryContext_1.ReactTelemetryContext, telemetryContext);
    const emitWarningTelemetryEvent = (0, useTelemetrySender_1.useTelemetrySender)({
        type: 'label_opened',
        category: 'warning',
        source: source !== null && source !== void 0 ? source : testId,
        content_type: content_Type !== null && content_Type !== void 0 ? content_Type : undefined,
    }, 'empty', telemetry);
    const emitCertificationTelemetryEvent = (0, useTelemetrySender_1.useTelemetrySender)({
        type: 'label_opened',
        category: 'certification',
        source: source !== null && source !== void 0 ? source : testId,
        content_type: content_Type !== null && content_Type !== void 0 ? content_Type : undefined,
    }, 'empty', telemetry);
    const emitSensitivityTelemetryEvent = (0, useTelemetrySender_1.useTelemetrySender)({
        type: 'label_opened',
        category: 'sensitivity',
        source: source !== null && source !== void 0 ? source : testId,
        content_type: content_Type !== null && content_Type !== void 0 ? content_Type : undefined,
    }, 'empty', telemetry);
    const emitCustomTelemetryEvent = (0, useTelemetrySender_1.useTelemetrySender)({
        type: 'label_opened',
        category: 'custom',
        source: source !== null && source !== void 0 ? source : testId,
        content_type: content_Type !== null && content_Type !== void 0 ? content_Type : undefined,
    }, 'empty', telemetry);
    return {
        emitWarningTelemetryEvent,
        emitCertificationTelemetryEvent,
        emitSensitivityTelemetryEvent,
        emitCustomTelemetryEvent,
    };
}
function usePanelContent(props, _a) {
    var _b;
    var { labelDetails } = _a, data = tslib_1.__rest(_a, ["labelDetails"]);
    if (!props.showing) {
        return null;
    }
    const value = labelDetails !== null && labelDetails !== void 0 ? labelDetails : { categories: {}, values: {} };
    const loading = data.loading || !data;
    if (props.showing === 'warning') {
        return (react_1.default.createElement(LabelHelpers_1.LabelPopoverDetailsContext.Provider, { value: value },
            react_1.default.createElement(LabelPopover_1.LabelPopover, { loading: loading, resolved: data === null || data === void 0 ? void 0 : data.warnings })));
    }
    else if (props.showing === 'sensitivity') {
        return (react_1.default.createElement(LabelHelpers_1.LabelPopoverDetailsContext.Provider, { value: value },
            react_1.default.createElement(LabelPopover_1.LabelPopover, { loading: loading, resolved: data === null || data === void 0 ? void 0 : data.sensitivities })));
    }
    else if (props.showing === 'certified') {
        return (react_1.default.createElement(LabelHelpers_1.LabelPopoverDetailsContext.Provider, { value: value },
            react_1.default.createElement(LabelPopover_1.LabelPopover, { loading: loading, resolved: data === null || data === void 0 ? void 0 : data.certifications })));
    }
    else if (props.showing) {
        return (react_1.default.createElement(LabelHelpers_1.LabelPopoverDetailsContext.Provider, { value: value },
            react_1.default.createElement(LabelPopover_1.LabelPopover, { loading: loading, resolved: (_b = data === null || data === void 0 ? void 0 : data.customLabels) === null || _b === void 0 ? void 0 : _b.get(props.showing) })));
    }
    else {
        return null;
    }
}
function useRenderLabelsForPanel(_a) {
    var { elevated: elevatedProp, renderMode } = _a, params = tslib_1.__rest(_a, ["elevated", "renderMode"]);
    const [state, dispatch] = usePanelReducer(renderMode, renderMode === 'panel' ? elevatedProp : false);
    const { element, data } = useRenderLabelsInner(Object.assign(Object.assign({}, params), { dispatch, renderMode }));
    const anySevereWarnings = isSevere(params.probablyOrEager, data === null || data === void 0 ? void 0 : data.warnings);
    const anySevereSensitivities = isSevere(params.probablyOrEager, data === null || data === void 0 ? void 0 : data.sensitivities);
    react_1.default.useEffect(() => {
        if (anySevereWarnings && anySevereSensitivities) {
            dispatch({ type: 'severe', category: 'warning' });
        }
        else if (anySevereWarnings) {
            dispatch({ type: 'severe', category: 'warning' });
        }
        else if (anySevereSensitivities) {
            dispatch({ type: 'severe', category: 'sensitivity' });
        }
    }, [anySevereWarnings, anySevereSensitivities, dispatch]);
    const panel = usePanelContent(state, data);
    return { element, panel };
}
exports.useRenderLabelsForPanel = useRenderLabelsForPanel;
function useRenderLabels(params) {
    return useRenderLabelsInner(Object.assign(Object.assign({}, params), { dispatch: () => {
            return;
        } }))
        .element;
}
exports.useRenderLabels = useRenderLabels;
function useRenderLabelsInner(_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    var { dispatch } = _a, params = tslib_1.__rest(_a, ["dispatch"]);
    const [invokeFetch, data] = useLabels(params);
    const invoke = react_1.default.useCallback((category) => {
        dispatch({ type: 'click', clicked: category });
        invokeFetch();
    }, [dispatch, invokeFetch]);
    const { emitWarningTelemetryEvent, emitCertificationTelemetryEvent, emitSensitivityTelemetryEvent, emitCustomTelemetryEvent, } = useBuildTelemetryEvents(params.content.contentType, params.testId, params.source, (_b = params.contextOverrides) === null || _b === void 0 ? void 0 : _b.telemetryContext);
    const [showMore, toggleShowMore] = (0, useToggle_1.useToggle)(false);
    const warnings = useWarnings(data, params, invoke, emitWarningTelemetryEvent);
    const certifications = useCertifications(data, params, invoke, emitCertificationTelemetryEvent);
    const sensitivities = useSensitivities(data, params, invoke, emitSensitivityTelemetryEvent);
    const customLabelElements = useCustom(data, params, invoke, emitCustomTelemetryEvent);
    const displayedCustomLabels = (_c = customLabelElements === null || customLabelElements === void 0 ? void 0 : customLabelElements.displayedCustomLabels) !== null && _c !== void 0 ? _c : null;
    const hiddenCustomLabels = (_d = customLabelElements === null || customLabelElements === void 0 ? void 0 : customLabelElements.hiddenCustomLabels) !== null && _d !== void 0 ? _d : null;
    const numCustomLabels = (_e = customLabelElements === null || customLabelElements === void 0 ? void 0 : customLabelElements.numCustomLabels) !== null && _e !== void 0 ? _e : 0;
    const warningElement = warnings ? react_1.default.createElement(Wrapper, null, warnings) : null;
    const certificationElement = certifications ? react_1.default.createElement(Wrapper, null, certifications) : null;
    const sensitivityElement = sensitivities ? react_1.default.createElement(Wrapper, null, sensitivities) : null;
    const customLabelsElement = (react_1.default.createElement(react_1.default.Fragment, null, customLabelElements === null || customLabelElements === void 0 ? void 0 :
        customLabelElements.displayedCustomLabels, customLabelElements === null || customLabelElements === void 0 ? void 0 :
        customLabelElements.hiddenCustomLabels));
    const showMoreButton = (numCustomLabels > ((_f = params.chipDisplayCount) !== null && _f !== void 0 ? _f : defaultChipDisplayCount))
        ? (react_1.default.createElement(Wrapper, null,
            react_1.default.createElement(widgets_1.ButtonWidget, { onClick: () => toggleShowMore(), id: 'showMoreTextButton', buttonType: widgets_1.ButtonType.Icon }, showMore ? catalog_messages_1.Messages.LabelCustomShowLess() : catalog_messages_1.Messages.LabelCustomShowMoreWithCount({
                MESSAGE_COUNT: (numCustomLabels - ((_g = params.chipDisplayCount) !== null && _g !== void 0 ? _g : defaultChipDisplayCount)).toString(),
            }))))
        : null;
    const showMoreButtonLink = (numCustomLabels > ((_h = params.chipDisplayCount) !== null && _h !== void 0 ? _h : defaultChipDisplayCount))
        ? (react_1.default.createElement(Wrapper, null,
            react_1.default.createElement("a", { href: params.content.contentUrl, rel: 'noreferrer' },
                react_1.default.createElement(widgets_1.ButtonWidget, { id: 'showMoreTextButton', buttonType: widgets_1.ButtonType.Icon }, showMore ? catalog_messages_1.Messages.LabelCustomShowLess() : catalog_messages_1.Messages.LabelCustomShowMoreWithCount({
                    MESSAGE_COUNT: (numCustomLabels - ((_j = params.chipDisplayCount) !== null && _j !== void 0 ? _j : defaultChipDisplayCount)).toString(),
                })))))
        : null;
    if (params.displayCategories) { // assumes either icon or grid experience
        const { warning, certification, sensitivity, custom } = params.displayCategories;
        if (((_k = params.controls) === null || _k === void 0 ? void 0 : _k.kind) === 'icon') {
            return {
                element: (react_1.default.createElement(react_1.default.Fragment, null,
                    react_1.default.createElement(LabelHelpers_1.LabelPopoverDetailsContext.Provider, { value: (_l = data.labelDetails) !== null && _l !== void 0 ? _l : { categories: {}, values: {} } },
                        certification ? certificationElement : null,
                        warning ? warningElement : null,
                        sensitivity ? sensitivityElement : null,
                        custom ? customLabelsElement : null))),
                data: data,
            };
        }
        return {
            element: (react_1.default.createElement(LabelHelpers_1.LabelPopoverDetailsContext.Provider, { value: (_m = data.labelDetails) !== null && _m !== void 0 ? _m : { categories: {}, values: {} } },
                react_1.default.createElement("div", { style: { display: 'inline-flex', gap: '8px' } },
                    certification ? certificationElement : null,
                    warning ? warningElement : null,
                    sensitivity ? sensitivityElement : null,
                    displayedCustomLabels,
                    showMore ? hiddenCustomLabels : null,
                    ((_o = params
                        .content
                        .contentType) === null || _o === void 0 ? void 0 : _o.toLowerCase()) === 'column'
                        ? showMoreButtonLink
                        : showMoreButton))),
            data: data,
        };
    }
    else {
        return {
            element: (react_1.default.createElement(LabelHelpers_1.LabelPopoverDetailsContext.Provider, { value: (_p = data.labelDetails) !== null && _p !== void 0 ? _p : { categories: {}, values: {} } },
                react_1.default.createElement(StickersFlexBox_1.FlexBoxDiv, null,
                    certifications,
                    warnings,
                    sensitivities,
                    displayedCustomLabels,
                    showMore ? hiddenCustomLabels : null,
                    showMoreButton))),
            data: data,
        };
    }
}
const Wrapper = react_1.default.memo((props) => react_1.default.createElement(Styles_1.StickerChipStyle, null, props.children));
//# sourceMappingURL=useRenderLabels.js.map